/**
 * API клиент.
 *
 * ЗАРЧИМ
 *  - Access token ЗӨВХӨН санах ойд. localStorage-д тавибал XSS-ээр алдагдана.
 *  - Refresh token нь httpOnly cookie — JS хүрэхгүй.
 *  - 401 ирвэл нэг удаа refresh хийгээд дахин оролдоно.
 */

const BASE = '/api/v1';

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
  }
}

let accessToken: string | null = null;
let onSessionLost: (() => void) | null = null;

export const setAccessToken = (t: string | null): void => { accessToken = t; };

/**
 * ⚠️ ЗӨВХӨН SSE-д. `fetch`-ийн урсгал нь `api()`-аар дамждаггүй тул
 *    header-ээ өөрөө барих хэрэгтэй. Токеныг өөр газар гаргахгүй —
 *    ялангуяа URL, localStorage, лог руу ХЭЗЭЭ Ч бичихгүй.
 */
export const getAccessToken = (): string | null => accessToken;
export const setSessionLostHandler = (fn: (() => void) | null): void => { onSessionLost = fn; };

interface Options {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

async function raw(path: string, opts: Options = {}): Promise<Response> {
  return fetch(BASE + path, {
    method: opts.method ?? 'GET',
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(opts.body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(opts.body === undefined ? {} : { body: JSON.stringify(opts.body) }),
    ...(opts.signal ? { signal: opts.signal } : {}),
    credentials: 'same-origin',
  });
}

/** Refresh cookie-гээр шинэ access token авна. Амжилтгүй бол false. */
export async function refreshSession(): Promise<boolean> {
  const res = await fetch(`${BASE}/auth/refresh`, { method: 'POST', credentials: 'same-origin' });
  if (!res.ok) return false;
  const data = (await res.json()) as { accessToken: string };
  accessToken = data.accessToken;
  return true;
}

async function parse<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const json: unknown = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const err = (json as { error?: ApiErrorBody } | null)?.error;
    throw new ApiError(res.status, err ?? { code: 'INTERNAL', message: 'Сүлжээний алдаа' });
  }
  return json as T;
}

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  return parse<T>(await send(path, opts));
}

/**
 * Файл татах (CSV г.м.).
 *
 * ⚠️ `<a href="/api/...">` ажиллахгүй — access token санах ойд тул холбоос
 *    токенгүй явж 401 авна. Токентой fetch-ээр аваад браузерт хадгалуулна.
 */
export async function download(path: string, fallbackName: string): Promise<void> {
  const res = await send(path);
  if (!res.ok) await parse(res); // алдааг ApiError болгож шидэнэ

  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? fallbackName;
  saveBlob(await res.blob(), name);
}

/** Хөтөчид файл хадгалуулна — серверээс татсан ч, хөтөч дээр үүсгэсэн ч (Excel). */
export function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  // Зарим хөтөч click-ийн дараа шууд цуцалбал татаж амжихгүй
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

async function send(path: string, opts: Options = {}): Promise<Response> {
  let res = await raw(path, opts);

  // Access token хугацаа дуусмагц чимээгүй сэргээнэ.
  // ⚠️ `/auth/refresh` ӨӨРӨӨ энэ салаанаас ЗААВАЛ гадуур байна. Цуцлагдсан
  //    сесс дээр refresh нь 401 UNAUTHENTICATED буцаадаг тул үүнийг хасвал
  //    refresh дотроосоо refresh дуудаж хязгааргүй давтана. Гар утасны
  //    `lib/api.ts` дээр яг үүнийг `skipRefresh`-ээр барьдаг.
  const isAuthCall = path.startsWith('/auth/login') || path.startsWith('/auth/refresh');
  if (res.status === 401 && !isAuthCall) {
    if (await refreshSession()) {
      res = await raw(path, opts);
    } else {
      accessToken = null;
      onSessionLost?.();
    }
  }

  return res;
}

// ---- Хариуны нийтлэг бүрхүүл ----
export interface Envelope<T> { data: T }
export interface PagedEnvelope<T> {
  data: T[];
  meta: { count: number; total: number; nextCursor: string | null };
}
